import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { whenRumReady } from '../init-rum';

/**
 * Renames each RUM view to the Next route template (`/offering/[slug]`), so
 * vitals aggregate per route rather than per URL. RUM already creates a view
 * per client-side navigation; only the name changes here.
 */
export const useRumViewName = (): void => {
  const router = useRouter();

  useEffect(() => {
    let isCancelled = false;
    let detach: (() => void) | undefined;

    // The SDK arrives in its own chunk, so it may not be ready on mount. Views
    // created before it loads are still named: the SDK replays buffered entries
    // and this effect renames the current view as soon as the promise resolves.
    void whenRumReady().then((rum) => {
      if (!rum || isCancelled) {
        return;
      }

      const setViewName = () => rum.setViewName(router.pathname);

      // Names whichever view is current — the initial one if the chunk landed
      // quickly, or a later one if the user navigated while it was loading.
      setViewName();

      // The subscription is load-bearing, not redundant with the effect: Next's
      // pages router is a stable singleton that mutates `pathname` in place, so
      // a `[router]` dependency never re-fires on navigation. Depending on
      // `router.pathname` instead would re-run the effect but also tear down
      // and rebuild the subscription on every route change.
      //
      // `pushState` fires before `routeChangeComplete`, so by the time this
      // runs the new view already exists and is the one being renamed.
      router.events.on('routeChangeComplete', setViewName);
      detach = () => router.events.off('routeChangeComplete', setViewName);
    });

    return () => {
      isCancelled = true;
      detach?.();
    };
  }, [router]);
};
